import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { GroupService } from './api/group.service';
import { AccountService } from './api/account.service';
import { FeatureService } from './api/feature.service';
import { PermissionService } from './api/permission.service';
import { InvitationService } from './api/invitation.service';
import { RoleService } from './api/role.service';
import { TenantAssignment } from './models/rbac.model';
import { RbacActions } from './rbac.actions';
import {
  selectAccountsFilters,
  selectAccountsPageSize,
  selectCurrentTenantUuid,
  selectGroupsFilters,
} from './rbac.selectors';

@Injectable()
export class RbacEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly groupSvc = inject(GroupService);
  private readonly accountSvc = inject(AccountService);
  private readonly featureSvc = inject(FeatureService);
  private readonly permissionSvc = inject(PermissionService);
  private readonly invitationSvc = inject(InvitationService);
  private readonly roleSvc = inject(RoleService);

  // ─── Search (debounced) ────────────────────────────────────────────────────

  searchAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.setAccountsQuery),
      map(({ query }) => query.trim()),
      debounceTime(300),
      distinctUntilChanged(),
      withLatestFrom(
        this.store.select(selectAccountsFilters),
        this.store.select(selectCurrentTenantUuid),
        this.store.select(selectAccountsPageSize),
      ),
      map(([search, filters, tenantUuid, size]) =>
        RbacActions.loadAccounts({
          tenantUuid: tenantUuid || undefined,
          search: search || undefined,
          filters,
          page: 0,
          size,
        }),
      ),
    ),
  );

  searchGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.setGroupsQuery),
      map(({ query }) => query.trim()),
      debounceTime(300),
      distinctUntilChanged(),
      withLatestFrom(
        this.store.select(selectGroupsFilters),
        this.store.select(selectCurrentTenantUuid),
      ),
      map(([q, filters, tenantUuid]) =>
        RbacActions.loadGroups({
          tenantUuid: tenantUuid || undefined,
          q: q || undefined,
          groupTypes: (filters['groupType'] as string[])?.length
            ? (filters['groupType'] as string[])
            : undefined,
        }),
      ),
    ),
  );

  // ─── Groups ────────────────────────────────────────────────────────────────

  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.loadGroups),
      switchMap(({ tenantUuid, q, groupTypes }) =>
        this.groupSvc.getGroups({ tenantUuid, q, groupTypes }).pipe(
          map((groups) => RbacActions.loadGroupsSuccess({ groups })),
          catchError((error) => of(RbacActions.loadGroupsFailure({ error }))),
        ),
      ),
    ),
  );

  createGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.createGroup),
      switchMap(({ payload }) =>
        this.groupSvc.createGroup(payload).pipe(
          map((group) => RbacActions.createGroupSuccess({ group })),
          catchError((error) => of(RbacActions.createGroupFailure({ error }))),
        ),
      ),
    ),
  );

  updateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.updateGroup),
      switchMap(({ groupName, payload, tenantUuid }) =>
        this.groupSvc.updateGroup(groupName, payload, tenantUuid).pipe(
          map((group) => RbacActions.updateGroupSuccess({ group })),
          catchError((error) => of(RbacActions.updateGroupFailure({ error }))),
        ),
      ),
    ),
  );

  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.deleteGroup),
      switchMap(({ groupName, groupUuid, tenantUuid }) =>
        this.groupSvc.deleteGroup(groupName, tenantUuid).pipe(
          map(() => RbacActions.deleteGroupSuccess({ groupUuid })),
          catchError((error) => of(RbacActions.deleteGroupFailure({ error }))),
        ),
      ),
    ),
  );

  assignGroupMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.assignGroupMember),
      switchMap(({ groupName, accountEmail, tenantUuid }) =>
        this.groupSvc.assignGroupMember(groupName, accountEmail, tenantUuid).pipe(
          map(() => RbacActions.assignGroupMemberSuccess()),
          catchError((error) => of(RbacActions.assignGroupMemberFailure({ error }))),
        ),
      ),
    ),
  );

  removeGroupMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.removeGroupMember),
      switchMap(({ groupName, accountEmail, tenantUuid }) =>
        this.groupSvc.removeGroupMember(groupName, accountEmail, tenantUuid).pipe(
          map(() => RbacActions.removeGroupMemberSuccess()),
          catchError((error) => of(RbacActions.removeGroupMemberFailure({ error }))),
        ),
      ),
    ),
  );

  // ─── Accounts ──────────────────────────────────────────────────────────────

  loadAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.loadAccounts),
      switchMap(({ tenantUuid, search, filters, page, size }) =>
        this.accountSvc.getAccounts(tenantUuid, search, page ?? 0, size ?? 20, filters ?? {}).pipe(
          map(({ accounts, totalElements }) =>
            RbacActions.loadAccountsSuccess({ accounts, totalElements, page: page ?? 0 }),
          ),
          catchError((error) => of(RbacActions.loadAccountsFailure({ error }))),
        ),
      ),
    ),
  );

  createAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.createAccount),
      switchMap(({ payload, tenantAssignments }) =>
        this.accountSvc.createAccount(payload).pipe(
          switchMap((account) => {
            if (!tenantAssignments?.length)
              return of(RbacActions.createAccountSuccess({ account }));
            return forkJoin(
              tenantAssignments.map((ta) =>
                this.accountSvc
                  .assignTenant(account.accountUuid, ta.tenantUuid, ta.tenantAdmin)
                  .pipe(
                    switchMap(() =>
                      ta.blocked
                        ? this.accountSvc.updateTenantAssignment(
                            account.accountUuid,
                            ta.tenantUuid,
                            {
                              blocked: true,
                            },
                          )
                        : of(void 0),
                    ),
                  ),
              ),
            ).pipe(
              switchMap(() =>
                this.applyRoleChanges(account.email ?? payload.email ?? '', tenantAssignments),
              ),
              map(() => RbacActions.createAccountSuccess({ account })),
              catchError((error) => of(RbacActions.createAccountFailure({ error }))),
            );
          }),
          catchError((error) => of(RbacActions.createAccountFailure({ error }))),
        ),
      ),
    ),
  );

  updateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.updateAccount),
      switchMap(({ accountUuid, payload, tenantAssignments }) =>
        this.accountSvc.updateAccount(accountUuid, payload).pipe(
          switchMap((account) => {
            if (!tenantAssignments) return of(RbacActions.updateAccountSuccess({ account }));
            const existingMap = new Map(
              (account.tenantAccounts ?? []).map((ta) => [
                ta.tenant?.tenantUuid,
                { tenantAdmin: !!ta.tenantAdmin, blocked: !!ta.blocked },
              ]),
            );
            // Tenants removed in the panel are absent from tenantAssignments: unassign them.
            const keptUuids = new Set(tenantAssignments.map((ta) => ta.tenantUuid));
            const unassignOps = [...existingMap.keys()]
              .filter(
                (tenantUuid): tenantUuid is string => !!tenantUuid && !keptUuids.has(tenantUuid),
              )
              .map((tenantUuid) => this.accountSvc.unassignTenant(accountUuid, tenantUuid));
            const assignOps = tenantAssignments.map((ta) => {
              if (existingMap.has(ta.tenantUuid)) {
                const cur = existingMap.get(ta.tenantUuid)!;
                if (cur.tenantAdmin !== ta.tenantAdmin || cur.blocked !== ta.blocked) {
                  return this.accountSvc.updateTenantAssignment(accountUuid, ta.tenantUuid, {
                    tenantAdmin: ta.tenantAdmin,
                    blocked: ta.blocked,
                  });
                }
                return of(void 0);
              }
              return this.accountSvc.assignTenant(accountUuid, ta.tenantUuid, ta.tenantAdmin).pipe(
                switchMap(() =>
                  ta.blocked
                    ? this.accountSvc.updateTenantAssignment(accountUuid, ta.tenantUuid, {
                        blocked: true,
                      })
                    : of(void 0),
                ),
              );
            });
            const ops = [...unassignOps, ...assignOps];
            const tenantOps$: Observable<unknown> = ops.length ? forkJoin(ops) : of(void 0);
            return tenantOps$.pipe(
              switchMap(() => this.applyRoleChanges(account.email, tenantAssignments)),
              map(() => RbacActions.updateAccountSuccess({ account })),
              catchError((error) => of(RbacActions.updateAccountFailure({ error }))),
            );
          }),
          catchError((error) => of(RbacActions.updateAccountFailure({ error }))),
        ),
      ),
    ),
  );

  // Runs after the tenant ops: a role belongs to a tenant the account may have just joined.
  private applyRoleChanges(
    accountEmail: string,
    tenantAssignments: TenantAssignment[],
  ): Observable<unknown> {
    const ops = tenantAssignments.flatMap((ta) => [
      ...(ta.removeRoles ?? []).map((role) =>
        this.roleSvc.unassignRole(role, accountEmail, ta.tenantUuid),
      ),
      ...(ta.addRoles ?? []).map((role) =>
        this.roleSvc.assignRole(role, accountEmail, ta.tenantUuid),
      ),
    ]);
    return ops.length ? forkJoin(ops) : of(void 0);
  }

  deleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.deleteAccount),
      switchMap(({ accountUuid }) =>
        this.accountSvc.deleteAccount(accountUuid).pipe(
          map(() => RbacActions.deleteAccountSuccess({ accountUuid })),
          catchError((error) => of(RbacActions.deleteAccountFailure({ error }))),
        ),
      ),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.resetAccountPassword),
      switchMap(({ accountUuid }) =>
        this.accountSvc.resetPassword(accountUuid).pipe(
          map(() => RbacActions.resetAccountPasswordSuccess()),
          catchError((error) => of(RbacActions.resetAccountPasswordFailure({ error }))),
        ),
      ),
    ),
  );

  blockAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.blockAccount),
      switchMap(({ accountUuid }) =>
        this.accountSvc.blockAccount(accountUuid).pipe(
          map(() => RbacActions.blockAccountSuccess({ accountUuid })),
          catchError((error) => of(RbacActions.blockAccountFailure({ error }))),
        ),
      ),
    ),
  );

  unblockAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.unblockAccount),
      switchMap(({ accountUuid }) =>
        this.accountSvc.unblockAccount(accountUuid).pipe(
          map(() => RbacActions.unblockAccountSuccess({ accountUuid })),
          catchError((error) => of(RbacActions.unblockAccountFailure({ error }))),
        ),
      ),
    ),
  );

  // ─── Feature Details ───────────────────────────────────────────────────────

  loadFeatureDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.loadFeatureDetails),
      switchMap(() =>
        this.featureSvc.getFeatureDetails().pipe(
          map((featureDetails) => RbacActions.loadFeatureDetailsSuccess({ featureDetails })),
          catchError((error) => of(RbacActions.loadFeatureDetailsFailure({ error }))),
        ),
      ),
    ),
  );

  // ─── Permissions ───────────────────────────────────────────────────────────

  loadPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.loadPermissions),
      switchMap(({ tenantUuid, accountUuid }) =>
        this.permissionSvc.getPermissions(tenantUuid, accountUuid).pipe(
          map((permissions) => RbacActions.loadPermissionsSuccess({ permissions })),
          catchError((error) => of(RbacActions.loadPermissionsFailure({ error }))),
        ),
      ),
    ),
  );

  grantPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.grantPermission),
      switchMap(({ tenantUuid, payload }) =>
        this.permissionSvc.grantPermission(tenantUuid, payload).pipe(
          map((permission) => RbacActions.grantPermissionSuccess({ permission })),
          catchError((error) => of(RbacActions.grantPermissionFailure({ error }))),
        ),
      ),
    ),
  );

  revokePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.revokePermission),
      switchMap(({ tenantUuid, accountUuid, featureDetailName, itemUuid }) =>
        this.permissionSvc
          .revokePermission(tenantUuid, accountUuid, featureDetailName, itemUuid)
          .pipe(
            map(() =>
              RbacActions.revokePermissionSuccess({ accountUuid, featureDetailName, itemUuid }),
            ),
            catchError((error) => of(RbacActions.revokePermissionFailure({ error }))),
          ),
      ),
    ),
  );

  // ─── Invitations ───────────────────────────────────────────────────────────

  loadInvitations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.loadInvitations),
      switchMap(({ tenantUuid }) =>
        this.invitationSvc.getInvitations(tenantUuid).pipe(
          map((invitations) => RbacActions.loadInvitationsSuccess({ invitations })),
          catchError((error) => of(RbacActions.loadInvitationsFailure({ error }))),
        ),
      ),
    ),
  );

  sendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.sendInvitation),
      switchMap(({ tenantUuid, payload }) =>
        this.invitationSvc.sendInvitation(tenantUuid, payload).pipe(
          map((invitation) => RbacActions.sendInvitationSuccess({ invitation })),
          catchError((error) => of(RbacActions.sendInvitationFailure({ error }))),
        ),
      ),
    ),
  );

  revokeInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacActions.revokeInvitation),
      switchMap(({ tenantUuid, invitationId }) =>
        this.invitationSvc.revokeInvitation(tenantUuid, invitationId).pipe(
          map(() => RbacActions.revokeInvitationSuccess({ invitationId })),
          catchError((error) => of(RbacActions.revokeInvitationFailure({ error }))),
        ),
      ),
    ),
  );
}
